import { visitorId } from './visitor-id';
import { devProxyPath } from './dev-kit';

// Read here, not imported from kit-client: kit-client imports this file to attach the code to leads.
const API_BASE = process.env.NEXT_PUBLIC_KIT_API_URL || 'https://api.kitagency.co';
const COMPANY_ID = process.env.NEXT_PUBLIC_KIT_COMPANY_ID || 'kit-agency';

/** Only kitagency.co is registered with KIT; elsewhere the call would just fail on CORS. */
function canReachKit(): boolean {
  const h = window.location.hostname;
  return h === 'kitagency.co' || h.endsWith('.kitagency.co');
}

/**
 * Referral partner attribution (KIT's Referral Program module).
 *
 * A partner shares `https://kitagency.co/?ref=CODE` (optionally `&camp=NAME`).
 * Three steps, all here, or referred visitors silently become unattributed:
 *   1. captureReferral(): on any landing page, read ?ref= / ?camp=, normalise.
 *   2. Confirm the code with KIT (POST referral/click). Only a code KIT says is
 *      valid is stored, in this browser, for 90 days. First touch wins: a stored
 *      confirmed code is never overwritten by a later ?ref=.
 *   3. referralFields(): the stored code, spread into every lead-creating call
 *      (leads, freebies, forms, growth lab subscribe).
 *
 * Nothing is stored when the browser sends Global Privacy Control. Every read
 * and write is wrapped: storage can be blocked, and that must never affect a
 * form submission. A bad or stale code never fails a submission; it just
 * credits no one.
 */

const KEY = 'kit_referral';
const NINETY_DAYS = 1000 * 60 * 60 * 24 * 90;
const COMPANY_KEY = process.env.NEXT_PUBLIC_KIT_API_KEY || '';

type Stored = { code: string; campaign?: string; at: number };

/** Uppercase, trimmed, 1-40 of A-Z 0-9 _ -, else null. */
export function normaliseCode(raw: string | null | undefined): string | null {
  const c = (raw || '').trim().toUpperCase();
  return /^[A-Z0-9_-]{1,40}$/.test(c) ? c : null;
}

function readStored(): Stored | null {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null') as Stored | null;
    return v && typeof v.code === 'string' && typeof v.at === 'number' && Date.now() - v.at < NINETY_DAYS ? v : null;
  } catch {
    return null;
  }
}

function gpcOn(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  } catch {
    return false;
  }
}

/**
 * Call on every navigation. Does nothing unless the address carries a usable
 * ?ref=, no confirmed code is stored yet, and the visitor has not opted out.
 */
export function captureReferral(pathname: string) {
  try {
    if (gpcOn() || readStored()) return;
    const q = new URLSearchParams(location.search);
    const code = normaliseCode(q.get('ref'));
    if (!code) return;
    const campaign = (q.get('camp') || '').trim().slice(0, 200) || undefined;
    // Local and preview origins are not registered with KIT: the call would only fail (CORS) and log an error.
    // In development it goes through the local relay instead, so the whole flow can be tested.
    const relay = devProxyPath('referral/click');
    if (!canReachKit() && !relay) return;

    const vid = visitorId();
    // Fire and forget: never block rendering, never retry.
    void fetch(relay ?? `${API_BASE}/api/${COMPANY_ID}/referral/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-company-key': COMPANY_KEY },
      body: JSON.stringify({ code, campaign, visitorId: vid || undefined, landingPage: pathname.split('?')[0].slice(0, 200) }),
      keepalive: true,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res: { valid?: boolean } | null) => {
        // Re-check: another tab may have stored a code while this call was in flight (first touch wins).
        if (res?.valid === true && !readStored()) {
          localStorage.setItem(KEY, JSON.stringify({ code, campaign, at: Date.now() } satisfies Stored));
        }
      })
      .catch(() => {});
  } catch {
    /* never affects the visitor */
  }
}

/** The confirmed code, ready to spread into a lead-creating request body. Empty when there is none. */
export function referralFields(): { referralCode?: string; referralCampaign?: string } {
  const s = readStored();
  if (!s) return {};
  return { referralCode: s.code.slice(0, 64), referralCampaign: s.campaign?.slice(0, 200) };
}
